import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  Clock,
  Activity,
  TrendingUp,
  Shield,
  Zap
} from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const StatusPage: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const services = [
    {
      name: "Website",
      status: "operational",
      uptime: "99.99%",
      responseTime: "45ms",
      lastIncident: "None in 30 days"
    },
    {
      name: "API",
      status: "operational",
      uptime: "99.95%",
      responseTime: "120ms",
      lastIncident: "None in 30 days"
    },
    {
      name: "Database",
      status: "operational",
      uptime: "99.98%",
      responseTime: "15ms",
      lastIncident: "None in 30 days"
    },
    {
      name: "Payment Processing",
      status: "operational",
      uptime: "99.97%",
      responseTime: "200ms",
      lastIncident: "None in 30 days"
    },
    {
      name: "File Storage",
      status: "operational",
      uptime: "99.96%",
      responseTime: "80ms",
      lastIncident: "None in 30 days"
    },
    {
      name: "Email Service",
      status: "operational",
      uptime: "99.94%",
      responseTime: "150ms",
      lastIncident: "None in 30 days"
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'outage':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800';
      case 'outage':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const overallStatus = "operational";
  const overallUptime = "99.97%";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-white to-brand-blue/10">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-brand-blue rounded-full shadow-lg">
                <Activity className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              System <span className="text-brand-blue">Status</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real-time status of Master Ji's platform and services
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overall Status */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <div className="flex items-center justify-center space-x-3 mb-4">
              {getStatusIcon(overallStatus)}
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(overallStatus)}`}>
                {overallStatus === 'operational' ? 'All Systems Operational' : 'System Issues Detected'}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Master Ji Platform Status
            </h2>
            <p className="text-gray-600 mb-4">
              Last updated: {currentTime.toLocaleString()}
            </p>
            <div className="flex items-center justify-center space-x-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{overallUptime}</div>
                <div className="text-sm text-gray-600">Uptime (30 days)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-blue">6</div>
                <div className="text-sm text-gray-600">Services</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">0</div>
                <div className="text-sm text-gray-600">Active Issues</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Status */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Service <span className="text-brand-blue">Status</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Detailed status of each component in our platform
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(service.status)}
                    <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(service.status)}`}>
                    {service.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Uptime</div>
                    <div className="font-semibold text-gray-900">{service.uptime}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Response Time</div>
                    <div className="font-semibold text-gray-900">{service.responseTime}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-gray-500">Last Incident</div>
                    <div className="font-semibold text-gray-900">{service.lastIncident}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Performance <span className="text-brand-blue">Metrics</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Key performance indicators and system health metrics
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg text-center"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Response Time</h3>
              <div className="text-3xl font-bold text-green-600 mb-2">85ms</div>
              <p className="text-gray-600 text-sm">Average across all services</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg text-center"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Security</h3>
              <div className="text-3xl font-bold text-blue-600 mb-2">100%</div>
              <p className="text-gray-600 text-sm">No security incidents</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl shadow-lg text-center"
            >
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Availability</h3>
              <div className="text-3xl font-bold text-yellow-600 mb-2">99.97%</div>
              <p className="text-gray-600 text-sm">Monthly uptime guarantee</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Incident History */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Incident <span className="text-brand-blue">History</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Recent incidents and their resolutions
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gray-50 rounded-xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">No Recent Incidents</h3>
            <p className="text-gray-600 mb-6">
              All systems have been operating normally for the past 30 days. 
              We're committed to maintaining this level of reliability.
            </p>
            <div className="text-sm text-gray-500">
              Last incident: None in the past 30 days
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-blue/5 to-brand-yellow/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Need <span className="text-brand-blue">Help</span>?
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              If you're experiencing issues or have questions, our support team is here to help
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/help-center"
                className="px-8 py-4 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 transition-colors font-medium"
              >
                Visit Help Center
              </a>
              <a
                href="/contact"
                className="px-8 py-4 border border-brand-blue text-brand-blue rounded-lg hover:bg-brand-blue/10 transition-colors font-medium"
              >
                Contact Support
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StatusPage;

